import { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useGetConsignmentsInfiniteQuery,
  useLazyExportConsignmentsQuery,
  type ConsignmentTableData
} from '@/entities/consignments';
import { useProjectSiteParams, useUrlState, usePermissions } from '@/shared/lib';

import { DEFAULT_URL_STATE } from './constants';

export const useConsignments = () => {
  const { t } = useTranslation();
  const { projectId, siteId, isValid } = useProjectSiteParams();
  const { canExportData } = usePermissions();
  const [urlState, setUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const queryParams = useMemo(
    () => ({
      projectId,
      siteId,
      ...urlState
    }),
    [projectId, siteId, urlState]
  );

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useGetConsignmentsInfiniteQuery(queryParams, {
      skip: !isValid
    });

  const tableData = useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page) => page.content) as ConsignmentTableData[];
  }, [data]);

  const lastPage = data?.pages?.[data.pages.length - 1];
  const totalElements = lastPage?.totalElements || 0;
  const totalPages = lastPage?.totalPages || 0;
  const currentPage = lastPage?.page || 0;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    setSelectedIds([]);
  }, [projectId, siteId]);

  const handleSort = useCallback(
    (
      sortField: string = DEFAULT_URL_STATE.sortField,
      sortDirection: 'asc' | 'desc' = DEFAULT_URL_STATE.sortDirection
    ) => {
      setUrlState({
        sortField,
        sortDirection
      });
    },
    [setUrlState]
  );

  const [triggerExport, { isLoading: isExporting }] = useLazyExportConsignmentsQuery();

  const handleExport = useCallback(async () => {
    const { size, ...exportFilters } = queryParams;

    try {
      await triggerExport({
        ...exportFilters,
        format: 'xlsx' // Defaulting to xlsx, but can be configured later
      }).unwrap();
      toast.success(t('consignments.exportSuccess', 'Consignments exported successfully'));
    } catch {
      toast.error(t('consignments.exportError', 'Failed to export consignments'));
    }
  }, [triggerExport, queryParams, t]);

  return {
    // Data
    tableData,
    totalElements,
    totalPages,
    currentPage,

    // Loading states
    isLoading: isLoading || (isFetching && !isFetchingNextPage),
    isFetchingNextPage,
    error,
    isValid,

    // Pagination
    hasMore: hasNextPage || false,
    handleLoadMore,

    // Selection
    selectedIds,
    setSelectedIds,

    // Sorting
    sortField: urlState.sortField,
    sortDirection: urlState.sortDirection as 'asc' | 'desc',
    handleSort,

    // Viewpoint — project-wide vs the current location group
    allSites: urlState.allSites === 'true',

    // Project/Site
    projectId,
    siteId,

    // Actions
    refetch,
    handleExport,
    isExporting,
    canExport: canExportData
  };
};
